"use client";

import { useQuery } from "@tanstack/react-query";
import { getAdminPassword } from "@/lib/auth/storage";
import { adminApi } from "@/lib/api/admin";
import type { RegisterFilter } from "@/lib/types";
import { isBeforeMorningFinalize, todayIST } from "@/lib/timezone";

export function useAdminRegister(date: string, filter: RegisterFilter) {
  const adminPassword = getAdminPassword();

  return useQuery({
    queryKey: ["admin", "register", date, filter],
    queryFn: () => adminApi.getRegister(adminPassword!, date, filter),
    enabled: !!adminPassword,
    refetchInterval: (query) => {
      const data = query.state.data;
      if (!data) return false;
      if (data.morning_finalized) return 60_000;
      if (isBeforeMorningFinalize()) return 30_000;
      return 60_000;
    },
  });
}

export function useAdminDashboard() {
  const adminPassword = getAdminPassword();
  const date = todayIST();
  return useQuery({
    queryKey: ["admin", "dashboard", date],
    queryFn: () => adminApi.getDashboard(adminPassword!, date),
    enabled: !!adminPassword,
    refetchInterval: 30_000,
  });
}

export function useAdminPeople() {
  const adminPassword = getAdminPassword();
  return useQuery({
    queryKey: ["admin", "people"],
    queryFn: () => adminApi.listPeople(adminPassword!),
    enabled: !!adminPassword,
  });
}

export function useAdminNotices(seen?: boolean) {
  const adminPassword = getAdminPassword();
  return useQuery({
    queryKey: ["admin", "notices", seen],
    queryFn: () => adminApi.listNotices(adminPassword!, seen),
    enabled: !!adminPassword,
  });
}

export function useAdminMonthly(month: string) {
  const adminPassword = getAdminPassword();
  return useQuery({
    queryKey: ["admin", "monthly", month],
    queryFn: () => adminApi.getMonthlyRegister(adminPassword!, month),
    enabled: !!adminPassword,
  });
}

export function useAdminHolidays(month: string) {
  const adminPassword = getAdminPassword();
  return useQuery({
    queryKey: ["admin", "holidays", month],
    queryFn: () => adminApi.listHolidays(adminPassword!, month),
    enabled: !!adminPassword,
  });
}

export function useAdminEmployeeShifts(date: string) {
  const adminPassword = getAdminPassword();
  return useQuery({
    queryKey: ["admin", "employee-shifts", date],
    queryFn: () => adminApi.listEmployeeShifts(adminPassword!, date),
    enabled: !!adminPassword,
  });
}
